import { useEffect, useRef, type RefObject } from 'react'

export interface UseLoadMoreOnScrollOptions<T extends HTMLElement = HTMLElement> {
  /** Whether there are more items to load */
  hasMore: boolean
  /** Called when the sentinel element comes into view (optional; when absent, no load is triggered) */
  onLoadMore?: () => void
  /** When true, prevents firing onLoadMore (e.g. while a page is already loading) */
  isLoading?: boolean
  /** When true, observer is not set up (e.g. during initial load) */
  disabled?: boolean
  /** Total number of items in the list; used to compute which item is the sentinel */
  itemCount: number
  /** Load more when this many items from the end scroll into view (default 10) */
  offsetFromEnd?: number
  /** IntersectionObserver rootMargin to trigger slightly before element is visible (default '100px') */
  rootMargin?: string
}

export interface UseLoadMoreOnScrollReturn<T extends HTMLElement = HTMLElement> {
  /** Attach this ref to the element at sentinelIndex to trigger load more when it enters view */
  sentinelRef: RefObject<T | null>
  /** Index of the item that should receive sentinelRef (Math.max(0, itemCount - offsetFromEnd)) */
  sentinelIndex: number
}

/**
 * Triggers a load-more callback when the Nth-from-last item in a list scrolls into view.
 * Use sentinelRef on the element at sentinelIndex to avoid loading too late and prevent double-fires.
 */
export function useLoadMoreOnScroll<T extends HTMLElement = HTMLElement>(
  options: UseLoadMoreOnScrollOptions<T>
): UseLoadMoreOnScrollReturn<T> {
  const {
    hasMore,
    onLoadMore,
    isLoading = false,
    disabled = false,
    itemCount,
    offsetFromEnd = 10,
    rootMargin = '100px',
  } = options

  const sentinelRef = useRef<T | null>(null)
  const sentinelIndex = Math.max(0, itemCount - offsetFromEnd)

  useEffect(() => {
    if (!hasMore || !onLoadMore || disabled) return

    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (!entry.isIntersecting) return
        if (isLoading) return
        onLoadMore()
      },
      { rootMargin }
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasMore, onLoadMore, disabled, isLoading, rootMargin])

  return { sentinelRef, sentinelIndex }
}
